import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useId,
  useRef,
  type ChangeEvent,
  type ReactNode,
} from 'react';

import { cn } from '@/shared/lib/utils';
import { Icon } from '@/shared/ui/icon';

export interface CheckboxProps {
  label?: ReactNode;
  checked?: boolean;
  defaultChecked?: boolean;
  /** When `true`, displays the mixed/indeterminate state. */
  indeterminate?: boolean;
  /** Phoenix-native change handler — receives the new boolean value. */
  onCheckedChange?: (checked: boolean) => void;
  /** Native `<input>` change handler — fired alongside `onCheckedChange`. */
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  required?: boolean;
  error?: ReactNode;
  helperText?: ReactNode;
  id?: string;
  name?: string;
  className?: string;
}

/**
 * Phoenix checkbox — compact 15×15 box with check / minus glyph, an optional
 * label, helper text and error message.
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  {
    label,
    checked,
    defaultChecked,
    indeterminate,
    onCheckedChange,
    onChange,
    disabled,
    required,
    error,
    helperText,
    id: externalId,
    name,
    className,
  },
  ref
) {
  const generatedId = useId();
  const checkboxId = externalId ?? generatedId;
  const messageId = `${checkboxId}-message`;
  const hasError = Boolean(error);
  const inputRef = useRef<HTMLInputElement>(null);

  useImperativeHandle(ref, () => inputRef.current as HTMLInputElement, []);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = Boolean(indeterminate);
    }
  }, [indeterminate]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange?.(event);
    onCheckedChange?.(event.target.checked);
  };

  const showCheck = indeterminate ? false : !!checked || !!defaultChecked;
  const showMinus = !!indeterminate;
  const filled = showCheck || showMinus;

  const boxClasses = cn(
    'phx-check-box rounded-phx-sm inline-flex h-[15px] w-[15px] items-center justify-center',
    'border transition-[background-color,border-color]',
    filled
      ? 'border-phx-primary bg-phx-primary text-phx-primary-fg'
      : 'border-phx-border-3 bg-phx-surface text-phx-primary-fg',
    hasError && 'border-phx-danger',
    'peer-focus-visible:shadow-phx-focus'
  );

  return (
    <div className={cn('phx-check-wrap gap-phx-2 grid', className)}>
      <label
        htmlFor={checkboxId}
        className={cn(
          'phx-check-label gap-phx-3 text-phx-md inline-flex items-center',
          disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
        )}
        data-disabled={disabled ? '' : undefined}
      >
        <span className="phx-check relative inline-flex">
          <input
            ref={inputRef}
            id={checkboxId}
            type="checkbox"
            checked={checked}
            defaultChecked={defaultChecked}
            disabled={disabled}
            required={required}
            name={name}
            onChange={handleChange}
            aria-invalid={hasError || undefined}
            aria-describedby={hasError || helperText ? messageId : undefined}
            className="peer absolute inset-0 h-full w-full cursor-[inherit] opacity-0"
          />
          <span className={boxClasses}>
            {showCheck ? <Icon name="check" size={10} strokeWidth={2.5} /> : null}
            {showMinus ? <Icon name="minus" size={10} strokeWidth={2.5} /> : null}
          </span>
        </span>
        {label ? <span className="text-phx-text">{label}</span> : null}
      </label>
      {error || helperText ? (
        <div
          id={messageId}
          data-error={hasError ? '' : undefined}
          className={cn(
            'phx-field-msg text-phx-xs pl-phx-7',
            hasError ? 'text-phx-danger' : 'text-phx-text-muted'
          )}
        >
          {error ?? helperText}
        </div>
      ) : null}
    </div>
  );
});
